import React, { useEffect } from 'react';
import { Grid, Alert } from '@mui/material';
import {
    Select,
    DateInput,
    Input,
    WeightInput,
    SearchableDropdown, 
    BinaryButtonGroup,
    InformationAlert
} from '../../Common';
import { testResultsOptions } from '../utils';
import { isMobile } from '../../../utils';
import LeakLocationAutocomplete from './LeakLocationAutocomplete';
import Enums from '../../../enums';
import { TranslateW } from "../../../Utils/Languages/Translate"
import { lightRed, red } from '../../../color_utils';


const FollowUpVerificationTest = ({
    formState,
    setGlobalState,
    dropdownOptions,
    requiredFieldsMissing:{
        verificationLeakInspection: verificationLeakInspectionErrors = {}
    },
    // setRequiredFields,
    fieldsWithErrors: {
        inspectionDate: {
            errorExists = false,
            helperText = "",
        } = {}
    },
    requiredFieldsRef,
}) => {
    // Leak Repair Status (leakRepairDispositionTypeId) is no longer entered here — it is DERIVED
    // from verification results in the parent (index.js, #14798). When it resolves to No Repair(1)
    // or Repair Attempted(2) the asset is Under Repair, so the Status After Service dropdown below
    // is locked. Read the derived value from its source of truth (verificationLeakInspection).
    const underRepairStatusIds = [
        Enums.SE.LeakRepairDispositionType.NoRepair,
        Enums.SE.LeakRepairDispositionType.RepairAttempted,
    ];
    const noLeakRepairOrRepairAttempted = underRepairStatusIds.includes(
        formState.verificationLeakInspection?.leakRepairDispositionTypeId
    );

    const setBasicRequiredFields = () => {
        requiredFieldsRef.current = {
            ...requiredFieldsRef.current,
            "FollowUpVerificationService": [
                "inspectionDate",
                "leakDetectionMethodId",
                "wasLeakFound",
            ]
        };
    };

    const deleteFollowUpLeakInspectionValues = () => {
        delete formState.verificationLeakInspection.leakLocationCategoryId;
        delete formState.verificationLeakInspection.leakLocationId;
        delete formState.verificationLeakInspection.faultCauseTypeId;
        delete formState.verificationLeakInspection.estimatedLeakAmount;
        setBasicRequiredFields();
    };

    const handleLeakFoundCircuitOpened = (leakFound, isNested) => {
        if (leakFound === true) {
            requiredFieldsRef.current = {
                ...requiredFieldsRef.current,
                "FollowUpVerificationService": [
                    "inspectionDate",
                    "leakDetectionMethodId",
                    "wasLeakFound",
                    "leakLocationId",
                    "faultCauseTypeId",
                ]
            };
        }

        if (leakFound === false) {
            deleteFollowUpLeakInspectionValues();
        }

        return setGlobalState("verificationLeakInspection.wasLeakFound", leakFound, isNested);
    };

    const handleLeakLocation = ({ leakLocationId, leakLocationCategoryId }) => {
        setGlobalState("verificationLeakInspection.leakLocationId", leakLocationId, true);
        setGlobalState("verificationLeakInspection.leakLocationCategoryId", leakLocationCategoryId, true);
    };

    useEffect(() => {
        handleLeakFoundCircuitOpened(formState.verificationLeakInspection.wasLeakFound, true);
    }, []);

    return (
        <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
                <DateInput
                    showTodayButton
                    allowPast
                    allowFuture={false}
                    label={`${TranslateW("leakRepair.date-followup-service")}*`}
                    date={formState.verificationLeakInspection.inspectionDate}
                    error={verificationLeakInspectionErrors.inspectionDate || errorExists}
                    customHelperText={helperText}
                    onChange={new_date => setGlobalState("verificationLeakInspection.inspectionDate", new_date, true)}
                />
            </Grid>
            {/* Leak Repair Status dropdown removed (#14798) — leakRepairDispositionTypeId is now
                derived from verification results in index.js, not manually entered here. */}
            <Grid item xs={12} md={6}>
                <Select
                    required
                    label={TranslateW("leakInspectionView.leak-detection-method")}
                    value={formState.verificationLeakInspection.leakDetectionMethodId}
                    onChange={e => setGlobalState("verificationLeakInspection.leakDetectionMethodId", e.target.value, true)}
                    options={dropdownOptions.leakDetectionMethods}
                    error={verificationLeakInspectionErrors.leakDetectionMethodId}
                />
            </Grid>
            <Grid item xs={12}>
                <BinaryButtonGroup
                    id="verificationLeakInspectionWasLeakFound"
                    required
                    label={TranslateW("leakRepair.verification-results")}
                    value={formState.verificationLeakInspection.wasLeakFound}
                    onChange={selectedValue => handleLeakFoundCircuitOpened(selectedValue, true)}
                    options={testResultsOptions.map(option => ({
                        value: option.id,
                        label: option.name
                    }))}
                    error={verificationLeakInspectionErrors.wasLeakFound}
                />
            </Grid>
            {formState.verificationLeakInspection.wasLeakFound === true && (
                <>
                    <Grid item xs={12} md={6}>
                        <LeakLocationAutocomplete
                            required
                            label={TranslateW("leakLocationAndCause.leak-location")}
                            leakLocations={dropdownOptions.leakLocations}
                            leakLocationCategories={dropdownOptions.leakLocationCategories}
                            leakLocationId={formState.verificationLeakInspection.leakLocationId}
                            onChange={handleLeakLocation}
                            error={verificationLeakInspectionErrors.leakLocationId}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <SearchableDropdown
                            required
                            label={TranslateW("leakLocationAndCause.cause-of-leak-for-asset")}
                            value={formState.verificationLeakInspection.faultCauseTypeId}
                            onChange={id => setGlobalState("verificationLeakInspection.faultCauseTypeId", id, true)}
                            error={verificationLeakInspectionErrors.faultCauseTypeId}
                            options={dropdownOptions.leakCauses}
                            searchFields={['name']}
                        />
                    </Grid>
                    <Grid container spacing={2} item xs={12} md={6}>
                        <WeightInput
                            removePadding
                            showConversionArrows
                            columnSpan={5}
                            header={TranslateW("EstimatedLeakAmount") }
                            variant="standard"
                            value={formState.verificationLeakInspection.estimatedLeakAmount}
                            onChange={new_weight => setGlobalState("verificationLeakInspection.estimatedLeakAmount", new_weight, true)}
                        />
                    </Grid>
                </>
            )}
            <Grid item xs={12} md={6}>
                <Input
                    fullWidth
                    multiLine
                    label={TranslateW("Notes") }
                    value={formState.verificationLeakInspection.notes}
                    onChange={e => setGlobalState("verificationLeakInspection.notes", e.target.value, true)}
                />
                {formState.verificationLeakInspection.leakTestPassFail === 1 && (
                    <Alert severity="warning">
                        {TranslateW("ActualGasSection") }
                    </Alert>
                )}
            </Grid>
            <Grid item xs={12} md={6}>
                <Select
                    label={TranslateW("generalService.status-after-service") }
                    value={formState.finalCoolingApplianceStatusId}
                    onChange={e => setGlobalState("finalCoolingApplianceStatusId", e.target.value)}
                    options={dropdownOptions.afterServiceStatusOptions}
                    disabled={
                        formState.finalCoolingApplianceStatusId === Enums.SE.CoolingApplianceStatus.UnderRepair
                        && noLeakRepairOrRepairAttempted
                    }
                />
            </Grid>
            {formState.repairOutcomeTypeId === Enums.SE.RepairOutcomeType.LeakNotRepaired
                && formState.initialVerificationLeakInspection.wasLeakFound === false
                && formState.verificationLeakInspection.wasLeakFound === true && (
                <Grid item xs={12} md={12} lg={12}>
                    <Alert severity="info">
                        {TranslateW("repairOutcome.SubGuidance.FvtFail")}
                    </Alert>
                </Grid>
            )}
        </Grid>
    );
};

export default FollowUpVerificationTest;
